import { useState } from "react"
import { Chip } from "@/components/Molecules/Chip/Chip"
import { Icon } from "@/components/Atoms/Icon"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"

interface StateOption {
  nome: string
  sigla: string
}

interface CategoryOption {
  catappId: number
  catappDescricao: string
}

interface SubcategoryOption {
  subcatappId: number
  subcatappDescricao: string
  subcatappCatappId: number
}

export interface NetworkingFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  stateFilter: string
  onStateFilterChange: (value: string) => void
  states: StateOption[]
  categoriaFilter: string
  onCategoriaFilterChange: (value: string) => void
  subcategoriaFilter: string
  onSubcategoriaFilterChange: (value: string) => void
  categories: CategoryOption[]
  subcategories: SubcategoryOption[]
}

export const NetworkFilters = ({
  search,
  onSearchChange,
  stateFilter,
  onStateFilterChange,
  states,
  categoriaFilter,
  onCategoriaFilterChange,
  subcategoriaFilter,
  onSubcategoriaFilterChange,
  categories,
  subcategories,
}: NetworkingFiltersProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draftState, setDraftState] = useState("todos")
  const [draftCategoria, setDraftCategoria] = useState("todos")
  const [draftSubcategoria, setDraftSubcategoria] = useState("todos")

  const hasActiveFilters =
    stateFilter !== "todos" || categoriaFilter !== "todos" || subcategoriaFilter !== "todos"

  const selectedState = states.find((s) => s.sigla === stateFilter)
  const stateLabel = selectedState ? `${selectedState.sigla} - ${selectedState.nome}` : null

  const selectedCategory = categories.find((c) => String(c.catappId) === categoriaFilter)
  const categoryLabel = selectedCategory?.catappDescricao ?? null

  const selectedSubcategory = subcategories.find(
    (s) => String(s.subcatappId) === subcategoriaFilter
  )
  const subcategoryLabel = selectedSubcategory?.subcatappDescricao ?? null

  const filteredSubcategories =
    categoriaFilter !== "todos"
      ? subcategories.filter((s) => String(s.subcatappCatappId) === categoriaFilter)
      : []

  const draftFilteredSubcategories =
    draftCategoria !== "todos"
      ? subcategories.filter((s) => String(s.subcatappCatappId) === draftCategoria)
      : []

  const openDrawer = () => {
    setDraftState(stateFilter)
    setDraftCategoria(categoriaFilter)
    setDraftSubcategoria(subcategoriaFilter)
    setDrawerOpen(true)
  }

  const applyFilters = () => {
    onStateFilterChange(draftState)
    onCategoriaFilterChange(draftCategoria)
    onSubcategoriaFilterChange(draftSubcategoria)
    setDrawerOpen(false)
  }

  const clearFilters = () => {
    onStateFilterChange("todos")
    onCategoriaFilterChange("todos")
    onSubcategoriaFilterChange("todos")
    setDraftState("todos")
    setDraftCategoria("todos")
    setDraftSubcategoria("todos")
    setDrawerOpen(false)
  }

  const handleCategoriaChange = (value: string) => {
    onCategoriaFilterChange(value)
    onSubcategoriaFilterChange("todos")
  }

  const activeChips: { key: string; label: string; onRemove: () => void }[] = []

  if (stateFilter !== "todos" && stateLabel) {
    activeChips.push({
      key: "state",
      label: stateLabel,
      onRemove: () => onStateFilterChange("todos"),
    })
  }

  if (categoriaFilter !== "todos" && categoryLabel) {
    activeChips.push({
      key: "categoria",
      label: categoryLabel,
      onRemove: () => {
        onCategoriaFilterChange("todos")
        onSubcategoriaFilterChange("todos")
      },
    })
  }

  if (subcategoriaFilter !== "todos" && subcategoryLabel) {
    activeChips.push({
      key: "subcategoria",
      label: subcategoryLabel,
      onRemove: () => onSubcategoriaFilterChange("todos"),
    })
  }

  return (
    <div>
      {/* Mobile layout */}
      <div className="lg:hidden">
        <div className="flex items-center gap-3">
          <div className="relative flex flex-1 items-center gap-3 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Pesquisar por nome ou empresa..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2.5 focus:outline-none"
            />
          </div>
          <button
            onClick={openDrawer}
            className="relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-black/40 transition-colors"
          >
            <i
              className={`fi fi-ts-sliders-v flex items-center justify-center text-base ${hasActiveFilters ? "text-orange-500" : "text-white"}`}
            />
            {hasActiveFilters && (
              <span className="absolute -right-1 -top-1 flex size-3 items-center justify-center rounded-full bg-orange-500" />
            )}
          </button>
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="relative flex w-80 items-center gap-4 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Pesquisar por nome ou empresa..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2 focus:outline-none"
            />
          </div>

          <Select onValueChange={onStateFilterChange} value={stateFilter}>
            <SelectTrigger
              className={`w-52 border-border bg-surface ${stateFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todos os Estados" />
            </SelectTrigger>
            <SelectContent className="max-h-60 border-border bg-surface">
              <SelectItem value="todos">Todos os Estados</SelectItem>
              {states.map((s) => (
                <SelectItem key={s.sigla} value={s.sigla}>
                  {s.sigla} - {s.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select onValueChange={handleCategoriaChange} value={categoriaFilter}>
            <SelectTrigger
              className={`w-52 border-border bg-surface ${categoriaFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todas as Categorias" />
            </SelectTrigger>
            <SelectContent className="max-h-60 border-border bg-surface">
              <SelectItem value="todos">Todas as Categorias</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.catappId} value={String(c.catappId)}>
                  {c.catappDescricao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            onValueChange={onSubcategoriaFilterChange}
            value={subcategoriaFilter}
            disabled={categoriaFilter === "todos"}
          >
            <SelectTrigger
              className={`w-52 border-border bg-surface ${subcategoriaFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todas as Subcategorias" />
            </SelectTrigger>
            <SelectContent className="max-h-60 border-border bg-surface">
              <SelectItem value="todos">Todas as Subcategorias</SelectItem>
              {filteredSubcategories.map((s) => (
                <SelectItem key={s.subcatappId} value={String(s.subcatappId)}>
                  {s.subcatappDescricao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-orange-500"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="border-white/10 bg-surface px-4 pb-2">
          <DrawerHeader>
            <DrawerTitle className="text-white">Filtros</DrawerTitle>
          </DrawerHeader>

          <div className="flex flex-col gap-4 px-4 pb-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Estado</span>
              <Select onValueChange={setDraftState} value={draftState}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftState !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todos os Estados" />
                </SelectTrigger>
                <SelectContent className="max-h-60 border-border bg-surface">
                  <SelectItem value="todos">Todos os Estados</SelectItem>
                  {states.map((s) => (
                    <SelectItem key={s.sigla} value={s.sigla}>
                      {s.sigla} - {s.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Categoria</span>
              <Select
                onValueChange={(value) => {
                  setDraftCategoria(value)
                  setDraftSubcategoria("todos")
                }}
                value={draftCategoria}
              >
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftCategoria !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todas as Categorias" />
                </SelectTrigger>
                <SelectContent className="max-h-60 border-border bg-surface">
                  <SelectItem value="todos">Todas as Categorias</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.catappId} value={String(c.catappId)}>
                      {c.catappDescricao}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Subcategoria</span>
              <Select
                onValueChange={setDraftSubcategoria}
                value={draftSubcategoria}
                disabled={draftCategoria === "todos"}
              >
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftSubcategoria !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todas as Subcategorias" />
                </SelectTrigger>
                <SelectContent className="max-h-60 border-border bg-surface">
                  <SelectItem value="todos">Todas as Subcategorias</SelectItem>
                  {draftFilteredSubcategories.map((s) => (
                    <SelectItem key={s.subcatappId} value={String(s.subcatappId)}>
                      {s.subcatappDescricao}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DrawerFooter>
            <Button
              onClick={applyFilters}
              className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600"
            >
              Aplicar filtro
            </Button>
            <Button
              onClick={clearFilters}
              variant="ghost"
              className="w-full text-muted-foreground hover:text-white"
            >
              Limpar filtro
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
